import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ObjectTable,
  type ObjectTableProps,
} from 'components/ObjectTable/ObjectTable';
import {
  SelectObjectsModal,
  type SelectObjectsModalProps,
} from 'components/SelectObjectsModal/SelectObjectsModal';
import { getText } from 'i18n/intl';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import { type TFindObjectsPayload, findMoreObjects } from 'slices/object.slice';
import type { TTEObject, TType } from '@timeedit/types';
import { Button, Icon } from '@timeedit/ui-components';

export type SyncObjectTableProps = {
  objectIds: number[];
  objectType: TType;
  onChange: SelectObjectsModalProps['onClose'];
  onRemove: (objects: TTEObject[]) => void;
};

export const SyncObjectTable = ({
  objectIds,
  objectType,
  onChange,
  onRemove,
}: SyncObjectTableProps) => {
  const [isAddObjectModalOpen, setIsAddObjectModalOpen] = useState(false);
  const [objects, setObjects] = useState<TTEObject[]>([]);
  const [selectedObjectIds, setSelectedObjectIds] = useState<number[]>([]);
  const [selectedObjects, setSelectedObjects] = useState<TTEObject[]>([]);

  const fetchObjects: ObjectTableProps['fetchFn'] = useCallback(
    async (payload: TFindObjectsPayload) => {
      if (objectIds.length === 0) {
        setObjects([]);
        return {
          results: [],
          page: 1,
          totalPages: 0,
          totalResults: 0,
          limit: payload.limit,
        };
      }

      const response = await findMoreObjects({
        ...payload,
        ids: objectIds,
      });

      setObjects((previousObjects) => {
        const newObjects = response?.results ?? [];
        return [...previousObjects, ...newObjects];
      });

      return response;
    },
    [objectIds]
  );

  const handleRemove = useCallback(
    (objects: TTEObject[]) => {
      setSelectedObjectIds((prev) =>
        prev.filter((id) => !objects.some((object) => object.id === id))
      );

      setSelectedObjects((prev) =>
        prev.filter((object) => !objects.some((o) => o.id === object.id))
      );

      onRemove(objects);
    },
    [onRemove]
  );

  const contextMenuActions: ObjectTableProps['contextMenuActions'] = useMemo(
    () => ({
      itemLabel: (item) => item.extId,
      actions: [
        {
          key: 'delete',
          label: (
            <span style={{ color: 'red' }}>{getText('ui.action.delete')}</span>
          ),
          icon: (
            <Icon id="DeleteOutlined" alt="Delete" style={{ color: 'red' }} />
          ),
          onActionClick: (object) => handleRemove([object]),
        },
      ],
    }),
    [handleRemove]
  );

  const footerContent: ObjectTableProps['footerContent'] = useMemo(
    () => [
      <Button
        key="add-object"
        type="primary"
        onClick={() => setIsAddObjectModalOpen(true)}
      >
        {getText('ui.action.add')}
      </Button>,
      <Button
        key="remove-objects"
        type="default"
        disabled={selectedObjects.length === 0}
        onClick={() => {
          handleRemove(selectedObjects);
        }}
      >
        {getText('ui.action.remove')}
      </Button>,
    ],
    [handleRemove, selectedObjects]
  );

  const resetFn: ObjectTableProps['resetFn'] = useCallback(() => {
    setObjects([]);
  }, []);

  useEffect(() => {
    // Reset objects when the component mounts or when objectIds change
    resetFn();
  }, [resetFn, objectIds]);

  const onClose: SelectObjectsModalProps['onClose'] = useCallback(
    (selectedObjects) => {
      setIsAddObjectModalOpen(false);

      const selectedObjectIds = selectedObjects
        ? selectedObjects.map((object) => object.id)
        : [];

      if (
        selectedObjects != null &&
        // Don't treat it as a change if the selected objects are the same as the current ones
        !isEqual(sortBy(selectedObjectIds), sortBy(objectIds))
      ) {
        onChange(selectedObjects);
      }
    },
    [onChange, objectIds]
  );

  const rowSelection: ObjectTableProps['rowSelection'] = useMemo(
    () => ({
      selectedRowKeys: selectedObjectIds,
      onChange: (selectedRowKeys, selectedRows) => {
        setSelectedObjectIds(selectedRowKeys as number[]);
        setSelectedObjects(selectedRows as TTEObject[]);
      },
      preserveSelectedRowKeys: true,
      hideSelectAll: true,
      columnWidth: 32,
    }),
    [selectedObjectIds]
  );

  return (
    <>
      <ObjectTable
        tableId="objects.syncObjectsTable"
        availableObjectTypes={[objectType]}
        objects={objects}
        contextMenuActions={contextMenuActions}
        footerContent={footerContent}
        fetchFn={fetchObjects}
        resetFn={resetFn}
        totalItemCount={objectIds.length}
        scroll={{ y: '80vh' }}
        rowSelection={rowSelection}
      />
      <SelectObjectsModal
        selectedObjectIds={objectIds}
        availableObjectTypes={[objectType]}
        open={isAddObjectModalOpen}
        onClose={onClose}
      />
    </>
  );
};
